import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useVendorAuth } from './VendorAuthProvider';
import {
  LayoutDashboard,
  FileText,
  Building,
  CreditCard,
  Upload,
  User,
  LogOut,
  Menu,
  X,
  CheckCircle,
  ChevronRight,
} from 'lucide-react';

interface VendorDashboardLayoutProps {
  children: React.ReactNode;
}

const menuItems = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard, path: '/vendor/dashboard' },
  { id: 'profile', label: 'Business Details', icon: Building, path: '/vendor/dashboard/profile' },
  { id: 'categories', label: 'Categories & Services', icon: FileText, path: '/vendor/dashboard/categories' },
  { id: 'banking', label: 'Banking & References', icon: CreditCard, path: '/vendor/dashboard/banking' },
  { id: 'documents-registration', label: 'Business Registration & Identity', icon: Upload, path: '/vendor/dashboard/documents-registration' },
  { id: 'documents-compliance', label: 'Compliance & Certifications', icon: Upload, path: '/vendor/dashboard/documents-compliance' },
  { id: 'contact', label: 'Contact & Declaration', icon: User, path: '/vendor/dashboard/contact' },
];

const VendorDashboardLayout: React.FC<VendorDashboardLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { vendor, logout } = useVendorAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/vendor/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isActive = (path: string) => {
    if (path === '/vendor/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const currentPage = menuItems.find((item) => isActive(item.path));

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-gray-900/50 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={`fixed top-0 left-0 z-30 h-full w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:z-auto ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-5 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <img src="/logo192.png" alt="Logo" className="h-9 w-9 object-contain" />
              <div>
                <h1 className="text-sm font-bold text-gray-900 leading-tight">Vendor Portal</h1>
                <p className="text-xs text-gray-500">Sunbeth Global</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-md text-gray-500 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? 'bg-sunbeth-orange-50 text-sunbeth-orange-700 border border-sunbeth-orange-200'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className={`h-5 w-5 flex-shrink-0 ${active ? 'text-sunbeth-orange-600' : 'text-gray-400'}`} />
                  <span className="truncate text-left">{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="p-4 border-t border-gray-200 space-y-3">
            <div className="px-2 py-2 rounded-lg bg-gray-50">
              <p className="text-xs font-medium text-gray-900 truncate">
                {vendor?.displayName || vendor?.email || 'Vendor'}
              </p>
              <p className="text-xs text-gray-500 truncate">{vendor?.email}</p>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
            >
              <LogOut className="h-5 w-5 text-gray-400" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 truncate">
                  {currentPage?.label || 'Dashboard'}
                </h2>
                <div className="hidden sm:flex items-center text-xs text-gray-500 gap-1">
                  <span>Vendor</span>
                  <ChevronRight className="h-3 w-3" />
                  <span>{currentPage?.label || 'Overview'}</span>
                </div>
              </div>
            </div>
            <div className="hidden lg:flex items-center text-sm text-gray-600">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
              <span>Account Active</span>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 overflow-auto">{children}</main>
      </div>
    </div>
  );
};

export default VendorDashboardLayout;
